import {
  LogIcon,
  PencilIcon,
  PlusIcon,
  SyncIcon,
  TrashIcon,
  XIcon
} from "@primer/octicons-react";
import {
  ActionList,
  Box,
  Checkbox,
  Flash,
  IconButton,
  Label,
  TextInput,
  TreeView
} from "@primer/react";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { khoanNopApi } from "../../../api/khoanNopApi";
import { mucpPhaiThuApi } from "../../../api/mucPhaiThuApi";
import Button from "../../../components/ui/button";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { IHocSinh } from "../../../models/response/crm-hocsinh/IHocsinh";
import { IKhoanNopChungTuVm, IKhoanNopTreeView, IKhoanNopVm } from "../../../models/response/finance/IKhoanNop";
import { IMucphaiThuInsert, IMucphaiThuVm } from "../../../models/response/finance/IMucPhaiThu";

interface HinhThucMienGiamProps {
  hocSinh: IHocSinh;
  canEdit?: boolean;
}

interface MienGiamItem {
  id: number;
  ma_hinh_thuc: string;
  hinh_thuc_mien_giam: string;
  nhom_mien_giam: string;
}


const GhiDanhPage: React.FC<HinhThucMienGiamProps> = ({ hocSinh, canEdit = true }) => {
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const { translate } = useCommonContext();
  const [khoanNops, setKhoanNops] = useState<IKhoanNopVm[]>([]);
  const [khoanNopChungTus, setKhoanNopChungTus] = useState<IKhoanNopChungTuVm[]>([]);
  const [chungTuHocSinhs, setChungTuHocSinhs] = useState<IMucphaiThuVm[]>([]);

  const [khoanNopChungTuTreeViews, setKhoanNopChungTuTreeViews] = useState<IKhoanNopTreeView[]>([]);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [isTaoGhiDanh, setIsTaoGhiDanh] = useState<boolean>(false);

  useEffect(() => {
    if (nam_hoc && hocSinh) {
      getDanhSachChungTuByHocSinhs();
      getKhoanNopByHocSinhs();
    }
  }, [nam_hoc, hocSinh, dm_coso_id]);
  const getKhoanNopByHocSinhs = async () => {
    const res = await khoanNopApi.selectAllByHocSinh(hocSinh.id, nam_hoc);
    if (res.is_success) {
      setKhoanNops(res.data)
    }
    else {
      NotifyHelper.Error(res.message ?? "Error");
    }
  };
  const getDanhSachChungTuByHocSinhs = async () => {
    const res = await mucpPhaiThuApi.selectByHocSinh(hocSinh.id, nam_hoc);
    if (res.is_success) {
      setChungTuHocSinhs(res.data)
    }
    else {
      NotifyHelper.Error(res.message ?? "Error");
    }
  };
  useEffect(() => {
    const updatedKhoanNopChungTuTreeViews = khoanNops.map(khoanNop => {
      const isChildChecked = khoanNopChungTus.some(chungTu =>
        chungTu.sf_khoannop_id === khoanNop.id
      );
      const siblingKhoanNops = khoanNops.filter(
        (item) => item.sf_nhomkhoannop_id === khoanNop.sf_nhomkhoannop_id
      );
      const isParentChecked = siblingKhoanNops.every((sibling) =>
        khoanNopChungTus.some(
          (chungTu) =>
            chungTu.sf_khoannop_id === sibling.id &&
            chungTu.sf_nhomkhoannop_id === sibling.sf_nhomkhoannop_id
        )
      );
      const so_tien_phai_nop = khoanNopChungTus.find(chungTu =>
        chungTu.sf_khoannop_id === khoanNop.id
      )?.so_tien_phai_nop;

      return {
        ...khoanNop,
        child_checked: isChildChecked,
        parent_checked: isParentChecked,
        so_tien_phai_nop: so_tien_phai_nop ?? khoanNop.so_tien_dinh_muc
      };
    });

    setKhoanNopChungTuTreeViews(updatedKhoanNopChungTuTreeViews);
  }, [khoanNops, khoanNopChungTus]);
  const calculateTotalAmount = (views: IKhoanNopTreeView[]) => {
    return views.reduce((sum, item) => {
      return item.parent_checked || item.child_checked ? sum + item.so_tien_phai_nop : sum;
    }, 0);
  };
  useEffect(() => {
    setTotalAmount(calculateTotalAmount(khoanNopChungTuTreeViews));
  }, [khoanNopChungTuTreeViews]);
  const handleItemClick = (item: any) => {
  };
  const handleRemoveItem = (itemId: number) => {
  };
  const handleCheckboxClick = (event: any) => {
    event.stopPropagation();
  };
  const handleParentCheckboxChange = (parent: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setKhoanNopChungTus((prev) => updateKhoanNopChungTus(prev, parent, isChecked));
    parent.parent_checked = isChecked;
  };
  const updateKhoanNopChungTus = (prev: any[], parent: any, isChecked: boolean) => {
    if (isChecked) {
      // Trường hợp chuyển từ unchecked sang checked
      const updatedEntries = khoanNopChungTuTreeViews.filter(
        (item) =>
          item.sf_nhomkhoannop_id === parent.sf_nhomkhoannop_id
      ).map((subTree: any) => ({
        sf_nhomkhoannop_id: parent.sf_nhomkhoannop_id,
        sf_khoannop_id: subTree.id,
        so_tien_dinh_muc: subTree.so_tien_dinh_muc,
        so_tien_phai_nop: subTree.so_tien_phai_nop,
        sf_mucthuphi_id: subTree.sf_mucthuphi_id
      }));

      return [
        ...prev.filter(
          (item) =>
            item.sf_nhomkhoannop_id !== parent.sf_nhomkhoannop_id
        ),
        ...updatedEntries,
      ];
    } else {
      // Trường hợp chuyển từ checked sang unchecked
      return prev.filter(
        (item) => item.sf_nhomkhoannop_id !== parent.sf_nhomkhoannop_id
      );
    }
  };
  const inputRefs = useRef(new Map());
  const handleTuitionFeeChange = (subTree: IKhoanNopTreeView) => {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value.replace(/[^\d]/g, ''); // Loại bỏ ký tự không phải số
      const numericValue = value ? parseInt(value, 10) : 0;
      setKhoanNopChungTuTreeViews((prevTreeViews) =>
        prevTreeViews.map((item) =>
          item.id === subTree.id
            ? { ...item, so_tien_phai_nop: numericValue }
            : item
        )
      );
      setKhoanNopChungTus((prevTreeViews) =>
        prevTreeViews.map((item) =>
          item.sf_khoannop_id === subTree.id
            ? { ...item, so_tien_phai_nop: numericValue }
            : item
        )
      );
    };
  };
  const handleSubTaskCheckboxChange = (subTree: IKhoanNopTreeView) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;

    setKhoanNopChungTus((prev) => {
      const existingIndex = prev.findIndex(
        (chungTu) =>
          chungTu.sf_khoannop_id === subTree.id &&
          chungTu.sf_nhomkhoannop_id === subTree.sf_nhomkhoannop_id
      );

      if (checked) {
        // Nếu được check và chưa tồn tại, thêm mới
        if (existingIndex === -1) {
          return [
            ...prev,
            {
              sf_nhomkhoannop_id: subTree.sf_nhomkhoannop_id,
              sf_khoannop_id: subTree.id,
              so_tien_dinh_muc: subTree.so_tien_dinh_muc,
              so_tien_phai_nop: subTree.so_tien_phai_nop ?? subTree.so_tien_dinh_muc,
              sf_mucthuphi_id: subTree.sf_mucthuphi_id,
            },
          ];
        } else {
          // Nếu đã tồn tại, cập nhật so_tien_phai_nop
          const updated = [...prev];
          updated[existingIndex].so_tien_phai_nop = subTree.so_tien_phai_nop ?? subTree.so_tien_dinh_muc;
          return updated;
        }
      } else {
        // Nếu không được check, xóa khỏi danh sách
        return prev.filter(
          (chungTu) =>
            chungTu.sf_khoannop_id !== subTree.id ||
            chungTu.sf_nhomkhoannop_id !== subTree.sf_nhomkhoannop_id
        );
      }
    });
  };

  const hanldeSubmitGhiDanh = async () => {
    if (khoanNopChungTus) {
      const datasend: IMucphaiThuInsert = {
        dm_coso_id: dm_coso_id,
        nam_hoc: nam_hoc,
        ts_hocsinh_id: hocSinh.id,
        sf_mucphaithu_id: 0,
        KhoanPhaiThus: khoanNopChungTus
      }

      setIsSaving(true);
      const res = await mucpPhaiThuApi.save_ghi_danh(datasend);

      if (res.is_success) {
        NotifyHelper.Success(res.message || "Success");
        setKhoanNopChungTus([])
        getDanhSachChungTuByHocSinhs();
        setIsTaoGhiDanh(false)
      }
      else {
        NotifyHelper.Error(res.message ?? "Error");
      }
      setIsSaving(false);

    }
  }



  const parentTrees = useMemo(() => {
    if (khoanNopChungTuTreeViews.length === 0) return [];
    return Array.from(
      new Map(
        khoanNopChungTuTreeViews.map((item) => [
          item.sf_nhomkhoannop_id,
          {
            nhom_khoan_nop: item.nhom_khoan_nop,
            sf_nhomkhoannop_id: item.sf_nhomkhoannop_id,
            parent_checked: item.parent_checked,
          },
        ])
      ).values()
    );
  }, [khoanNopChungTuTreeViews]);
  const groupedSubTrees = useMemo(() => {
    const map = new Map();
    khoanNopChungTuTreeViews.forEach((item) => {
      if (!map.has(item.sf_nhomkhoannop_id)) {
        map.set(item.sf_nhomkhoannop_id, []);
      }
      map.get(item.sf_nhomkhoannop_id).push(item);
    });
    return map;
  }, [khoanNopChungTuTreeViews]);

  const treeViews = useMemo(() => {
    if (parentTrees.length === 0) return null;
    return (
      <TreeView aria-label="Issues">
        {parentTrees.map((parent, index) => (
          <TreeView.Item id={`${index}`} key={parent.sf_nhomkhoannop_id}>
            <TreeView.LeadingVisual>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Checkbox
                  id={`${index}`}
                  checked={parent.parent_checked}
                  disabled={!canEdit}
                  onChange={handleParentCheckboxChange(parent)}
                  onClick={handleCheckboxClick}
                  aria-label={`${parent.nhom_khoan_nop}`}
                />
              </Box>
            </TreeView.LeadingVisual>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '550px 1fr 1fr' }}>
              <span style={{ fontWeight: 600 }}>{parent.nhom_khoan_nop}</span>
              <span style={{ fontWeight: 600 }}>Số tiền định mức</span>
              <span style={{ fontWeight: 600 }}>Số tiền phải nộp</span>
            </Box>
            <TreeView.SubTree state="initial">
              {groupedSubTrees.get(parent.sf_nhomkhoannop_id).map((subTree: IKhoanNopTreeView, idxSubTree: number) => (
                <Box key={`${index}-${idxSubTree}`} sx={{ marginBottom: '3px' }}>
                  <TreeView.Item id={`${index}-${idxSubTree}`}>
                    <TreeView.LeadingVisual>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '3px' }}>
                        <Checkbox
                          id={`${idxSubTree}`}
                          checked={subTree.child_checked}
                          disabled={!canEdit}
                          onChange={handleSubTaskCheckboxChange(subTree)}
                          aria-label={`${subTree.ten_khoan_nop}`}
                        />
                      </Box>
                    </TreeView.LeadingVisual>
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '550px 1fr 1fr' }}>
                      <span style={{ marginTop: '5px' }}>{subTree.ten_khoan_nop}</span>
                      <span style={{ marginTop: '5px' }}>{subTree.so_tien_dinh_muc.toLocaleString('de-DE')}</span>
                      <TextInput
                        key={subTree.id}
                        aria-label="Số tiền phải nộp"
                        defaultValue={subTree.so_tien_phai_nop.toLocaleString('de-DE')}
                        onBlur={handleTuitionFeeChange(subTree)}  // Gọi hàm khi thay đổi giá trị
                        ref={(el) => inputRefs.current.set(subTree.id, el)}
                        sx={{ width: 100 }}
                        size="small"
                      />

                    </Box>
                  </TreeView.Item>
                </Box>
              ))}
            </TreeView.SubTree>
          </TreeView.Item>
        ))}
      </TreeView>
    );
  }, [parentTrees, groupedSubTrees]);

  return (
    <Box sx={{ padding: 0 }}>
      {error && (
        <Flash variant="danger" sx={{ mb: 2 }}>
          {error}
        </Flash>
      )}
      <Box sx={{ display: "flex", gap: 3 }}>
        <Box sx={{ width: 600 }}>

          <Box
            sx={{
              // bg: "canvas.subtle",
              p: 2,
              borderBottom: "1px solid",
              borderColor: "border.default",
              fontWeight: "bold",
              color: "fg.muted",
              height: "calc(100vh - 318px)",
              border: "1px solid",
              borderRadius: 2,
              overflowY: "auto",

            }}
          >
            <Box
              sx={{
                // bg: "canvas.subtle",
                pl: 2,
                borderBottom: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <h4>Danh sách chứng từ ghi danh</h4>
              <Box>
                <Button
                  sx={{ ml: 140 }}
                  text="Làm mới"
                  leadingVisual={SyncIcon}
                  size="small"
                  variant="default"
                  onClick={() => {
                    getDanhSachChungTuByHocSinhs();
                  }}
                />

              </Box>
              <Box>
                {!isTaoGhiDanh &&
                  <Button
                    sx={{ mr: 2 }}
                    text="Tạo ghi danh"
                    leadingVisual={PlusIcon}
                    size="small"
                    variant="primary"
                    onClick={() => {
                      setIsTaoGhiDanh(true)
                    }}
                  />
                }
                {isTaoGhiDanh &&
                  <Button
                    sx={{ mr: 2 }}
                    text="Hủy tạo ghi danh"
                    leadingVisual={XIcon}
                    size="small"
                    variant="danger"
                    onClick={() => {
                      setIsTaoGhiDanh(false)
                    }}
                  />
                }
              </Box>
            </Box>
            <ActionList showDividers>
              {chungTuHocSinhs.map((item) => (
                <ActionList.Item key={item.id} onClick={() => handleItemClick(item)}
                >
                  <ActionList.LeadingVisual>
                    <LogIcon size={14} />
                  </ActionList.LeadingVisual>
                  <Box sx={{ display: "flex", flex: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        width: "100%",
                        pr: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "flex",
                        justifyContent: "space-between",

                      }}
                    >
                      <Label title="Số chứng từ" variant="accent">{item.so_chung_tu}</Label>
                      <Label title="Tổng tiền các khoản nộp" variant="done">{item.tong_tien_phai_nop.toLocaleString()} VNĐ</Label>
                      {item.tong_tien_da_nop > 0 && <Label title="Trạng thái thanh toán" sx={{ mr: 5 }} variant="success">Đã thanh toán</Label>
                      }
                      {item.tong_tien_da_nop == 0 && <Label title="Trạng thái thanh toán" sx={{ mr: 5 }} variant="danger">Chưa thanh toán</Label>
                      }
                    </Box>
                  </Box>
                  <ActionList.Description variant="block">
                    Ngày tạo: {moment(item.ngay_tao).format('DD/MM/yyyy')} <br />
                    Người tạo: {item.nguoi_tao_username} <br />

                    Số tiền còn phải nộp: <span style={{ color: 'red' }}>{(item.tong_tien_phai_nop - item.tong_tien_da_nop).toLocaleString()}</span> VNĐ <br />
                    {/* Nội dung: {item.noi_dung} <br /> */}
                    {item.hinh_thuc_thanh_toan &&
                      <Box>
                        Hình thức thanh toán: {item.hinh_thuc_thanh_toan}
                      </Box>
                    }

                  </ActionList.Description>
                  {canEdit && (
                    <ActionList.TrailingVisual>
                      <IconButton
                        title="Sửa chứng từ"
                        size="small"
                        className="invisible"
                        icon={PencilIcon}
                        variant="default"
                        aria-label="Edit"
                        onClick={() => handleRemoveItem(item.id)}
                      />
                      <IconButton
                        title="Hủy chứng từ"
                        size="small"
                        sx={{ ml: 1 }}
                        className="invisible"
                        icon={TrashIcon}
                        variant="danger"
                        aria-label="Delete"
                        onClick={() => handleRemoveItem(item.id)}
                      />

                    </ActionList.TrailingVisual>
                  )}
                </ActionList.Item>
              ))}
            </ActionList>
          </Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          {isTaoGhiDanh && <Box>
            <Box
              sx={{
                bg: "canvas.subtle",
                p: 1,
                border: "1px solid",
                borderColor: "border.default",
                fontWeight: "bold",
                color: "fg.muted",
                borderRadius: 2,
                overflowY: "auto",

              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  Chi tiết các khoản thu chứng từ <span style={{ fontWeight: 500, color: 'red' }} >.............</span>
                </Box>
                <Box>
                  Tổng tiền: <span style={{ fontWeight: 500, color: 'red' }} >{totalAmount.toLocaleString('de-DE')}</span> VNĐ
                </Box>
                <Box>
                  <Button
                    text="Xác nhận"
                    leadingVisual={PlusIcon}
                    size="small"
                    variant="primary"
                    onClick={hanldeSubmitGhiDanh}
                    isLoading={isSaving}
                    disabled={khoanNopChungTus.length === 0}
                  />
                </Box>
              </Box>

            </Box>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "border.default",
                borderRadius: 2,
                height: "calc(100vh - 355px)",
                overflowY: "auto",
                mt: 0.5
              }}
            >
              {treeViews}
            </Box>
          </Box>}
        </Box>
      </Box >
    </Box >
  );
};

export default GhiDanhPage;
